import { useEffect } from 'react';
import { GitMerge } from 'lucide-react';

import {
  baseEdgeStackWebhookUrl,
  baseStackWebhookUrl,
} from '@/portainer/helpers/webhookHelper';
import { StackDeploymentInfo } from '@/react/common/stacks/types';
import { AutoUpdateResponse } from '@/react/portainer/gitops/types';

import { Icon } from '@@/Icon';
import { Card } from '@@/primitives/Card';

import { DivergenceAlert } from './DivergenceAlert';
import { GitReferenceErrorAlert } from './GitReferenceErrorAlert';
import { GitReferenceLineItems } from './GitReferenceLineItems';
import {
  GitReferenceStackType,
  useGitReferenceStatus,
} from './useGitReferenceStatus';

export function GitReferenceCard({
  stackType,
  stackId,
  autoUpdate,
  currentDeploymentInfo,
  workflowId,
  onErrorChange,
}: {
  stackType: GitReferenceStackType;
  stackId: number;
  autoUpdate?: AutoUpdateResponse | null;
  currentDeploymentInfo?: StackDeploymentInfo | null;
  workflowId: number;
  onErrorChange?: (hasError: boolean) => void;
}) {
  const { divergence, effective, status, error } = useGitReferenceStatus({
    workflowId,
    stackId,
    stackType,
    currentDeploymentInfo,
  });

  const webhookUrl = getWebhookUrl(stackType, autoUpdate);

  useEffect(() => {
    onErrorChange?.(!!error || status.isRefLoading || status.isFileLoading);
  }, [error, status.isRefLoading, status.isFileLoading, onErrorChange]);

  return (
    <Card.Container variant="filled" aria-label="Managed by Workflow">
      <Card.Body>
        <div className="form-section-title mt-0! flex items-center gap-2">
          <Icon icon={GitMerge} /> Managed by Workflow
        </div>
        {error && (
          <GitReferenceErrorAlert
            errorKind={error.kind}
            url={effective.url}
            reference={effective.ref}
            configFilePath={effective.configFilePath}
            infoMessage={error.message}
          />
        )}

        <GitReferenceLineItems
          workflowId={workflowId}
          stackType={stackType}
          config={effective}
          errorKind={error?.kind}
          status={status}
          webhookUrl={webhookUrl}
        />
        {divergence && <DivergenceAlert divergence={divergence} />}
      </Card.Body>
    </Card.Container>
  );
}

function getWebhookUrl(
  stackType: GitReferenceStackType,
  autoUpdate: AutoUpdateResponse | null | undefined
) {
  const { Webhook: webhook } = autoUpdate || {};
  if (!webhook) {
    return undefined;
  }

  const isEdge = stackType === 'edge' || stackType === 'edge-helm';

  const base = isEdge ? baseEdgeStackWebhookUrl() : baseStackWebhookUrl();

  return `${base}/${webhook}`;
}
